import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { AssignmentWithRelations } from '../../../../../api/app-types/assignment';
import { Lek } from '../../../../../api/app-types/lek.app';
import { CUSTOM_DATE_FORMATS } from '../../../../../core/adapters/custom-date-format';
import { LvrHeading } from '../../../../../core/components/heading/heading';
import { CarouselElement, NavCarousel } from '../../../../../core/components/nav-carousel/nav-carousel';
import { PaginatedResponse } from '../../../../../shared/pagination/paginated-response';
import { RouteInjection } from '../../../../../shared/routing/route-injection.class';
import { LekDeleteDialog } from './lek-delete-dialog';
import { ClientResolved } from '../../../../../shared/routing/resolvers/client.resolver';
import { hasPermission } from '../../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../../shared/storage/session-manager.service';
import { DB_PHASES } from '../../../../../api/app-types/phase.app';
import { ApiResponse, Metadata } from '../../../../../api/api-types/api-response.api';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import { createSuccessNotification } from '../../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-detail-lek',
	imports: [NavCarousel, LvrHeading, TranslocoPipe, DatePipe],
	templateUrl: './detail-lek.html',
	styleUrl: './detail-lek.scss',
})
export class DetailLek extends RouteInjection {
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	private readonly session = inject(SessionManagerService);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	public readonly client = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly lekWithMetadata = this.resolveDataAsSignal<ApiResponse<Lek, Metadata>>('lekWithMetadata');
	public readonly lek = computed(() => this.lekWithMetadata()?.data);
	private readonly lekMetadata = computed(() => this.lekWithMetadata()?.metadata);
	public readonly editableFields = computed(() => this.lekMetadata()?.editOnlyFields ?? []);
	public readonly leks = this.resolveDataAsSignal<PaginatedResponse<Lek>>('leks');
	public readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations | undefined>('assignment');
	private readonly dialog = inject(MatDialog);
	private readonly canEditRestricted = hasPermission(this.session.permissions, 'AssistanceResults.Edit.Restricted');
	private readonly canEditAll = hasPermission(this.session.permissions, 'AssistanceResults.Edit');
	private readonly canDeleteRestricted = hasPermission(this.session.permissions, 'AssistanceResults.Delete.Restricted');
	private readonly canDeleteAll = hasPermission(this.session.permissions, 'AssistanceResults.Delete');

	public readonly carouselElements = computed(() =>
		this.leks()?.entities.map((c) => ({
			id: c.id!.toString(),
			label: `${c.categoryAssistance.description}`,
		})),
	);

	public readonly currentIndex = computed(() => {
		const id = this.lek()?.id.toString();
		return this.carouselElements()?.findIndex((e) => e.id === id);
	});

	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../../'], { relativeTo: this.route }).toString(),
	);
	public readonly canDeleteLek = computed(() => this.canDeleteAll() || this.canDeleteRestricted());
	public readonly canEditLek = computed(() => this.canEditAll() || this.canEditRestricted());

	public readonly disableEditLekButton = computed(() => {
		if (this.assignment()?.phaseId !== DB_PHASES.P3) return true;
		if (this.canEditAll()) return false;
		if (this.canEditRestricted()) return !(this.editableFields()?.length ?? 0 > 0);
		return true;
	});

	public readonly disableDeleteLekButton = computed(() => {
		if (this.assignment()?.phaseId !== DB_PHASES.P3) return true;
		if (this.canDeleteAll()) return false;
		if (this.canDeleteRestricted()) return !(this.editableFields()?.length ?? 0 > 0);
		return true;
	});

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.route });
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public async removeLek(id: number | string) {
		if (!this.canDeleteLek()) return;

		const dialogRef = this.dialog.open(LekDeleteDialog, {
			data: {
				lekId: id,
				onDeleted: async () => {
					await this.router.navigate(['../../'], { relativeTo: this.route });
					this.ntfySrv.addNotification(
						createSuccessNotification(this.transloco.translate('base.notification_message.lek_delete_success')),
					);
				},
			},
		});

		await lastValueFrom(dialogRef.afterClosed());
	}
}
